import cjaAnalytics from 'yoda-core-components/lib/helpers/Analytics/CJAAnalytics';
import buildCJAPageViewPayload from 'yoda-core-components/lib/helpers/Analytics/CJAPageViewBuilder';
import { buildProductData } from 'yoda-core-components/lib/helpers/Analytics/CJAProductBuilder';
import buildCJAEvent from 'yoda-core-components/lib/helpers/Analytics/CJAEventBuilder';
import { decimalRounding } from 'yoda-site-components/lib/helpers/Pricing/Pricing';
import analyticsActions from 'yoda-site-components/lib/actionTypes/AnalyticsActionTypes';
import {
    initProductOptions,
    getSelectedColor,
    getSelectedSize,
} from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import productPricingSelector, {
    productPricingSelectorForAllLot,
} from 'yoda-site-components/lib/selectors/ProductPricingSelector';
import { selectMarketPlaceSellerInfo } from 'yoda-site-components/lib/selectors/MarketPlaceSelector';
import {
    selectProductDetailsWrapper,
    selectInventoryWrapper,
    selectSelectedQuantityWrapper,
} from 'yoda-site-components/lib/selectors/PdpNameSpaceSelector';
import productConstants from 'yoda-site-components/lib/common/ProductConstants';
import { ADD_SITE_TO_LIST_EVENT } from 'yoda-site-components/lib/actionTypes/SavedListToolTip';
import {
    PDP_PAGE_LOAD_READY,
    PDP_CART_ADD_ANALYTICS,
    BUNDLE_PDP_CART_ADD_ANALYTICS,
    PDP_CART_UPDATE_ANALYTICS,
    PDP_PRODUCT_OOS_ANALYTICS,
    PDP_FORM_ERROR_ANALYTICS,
    PDP_TOOL_CLICK_ANALYTICS,
    PDP_SAMEDAY_PICKUP_ANALYTICS,
    PDP_CHECK_AVAILABILITY_ANALYTICS,
    PDP_OPEN_MODAL_ANALYTICS,
    PDP_CHANGE_ZIPCODE_ANALYTICS,
    PDP_SHOP_THIS_LOOK_CLICK_EVENT,
    PDP_SHOP_THE_LOOK_MINI_PDP_CLICK_EVENT,
    PDP_SHOP_THE_SET_CLICK_EVENT,
    PDP_PSC_AVAILABILITY,
    PDP_CARE_PLAN_VIEW_ANALYTICS,
    PDP_CARE_PLAN_ACCEPT_ANALYTICS,
    SEARCH_STORES_CLICK_EVENT,
    PDP_PAGE_LOAD_PRODUCT_OOS,
    PDP_VIDEO_PLAYER_ANALYTICS,
    PDP_ENABLE_VTO,
} from '../actionTypes/AnalyticsActionTypes';

// --- Helpers ---

const getStoreAvailability = (channelOffering) => {
    if (channelOffering === 'Omnichannel') return 'both';
    if (channelOffering === 'OnlineOnly') return 'online only';
    return '';
};

const parsePriceString = (priceStr) => {
    if (!priceStr || !priceStr.length) return '0.00';
    const raw = priceStr.split('- ')[priceStr.split('- ').length - 1];
    return decimalRounding(raw.replace(/[$,]/g, ''));
};

const getPricingCadence = (type, marketingLabel, promotions) => {
    if (type && type.length !== 0) return type;
    if (marketingLabel || promotions) return 'buy more and save';
    return 'regular';
};

/**
 * Filters mapped product to selected lot only for lotId and lotDetails.
 * Used by cartAdd, cartUpdate, selectoptionspdp, carePlanView, carePlanAccept.
 */
const filterToSelectedLot = (mapped) => {
    const matchedLotId = mapped._matchedLotId;
    return {
        lotId: matchedLotId ? [matchedLotId] : mapped.lotId,
        lotDetails: matchedLotId
            ? (mapped.lotDetails || []).filter((ld) => ld.id === matchedLotId)
            : mapped.lotDetails,
    };
};

const buildLotDetailsFromPricing = (pricingForAllLots, lots) => {
    if (!pricingForAllLots || !pricingForAllLots.length) {
        return (lots || []).map((lot) => ({
            id: lot.id || '',
            numberOfSKU: (lot.items || []).length,
        }));
    }
    return pricingForAllLots.map((lotPricing) => {
        const {
            defaults,
            original,
            fpac,
            type,
            marketingLabel,
            promotions,
            lotId,
            mapPrice,
            isCouponAmount,
        } = lotPricing;
        const matchedLot = lots.find((l) => l.id === lotId) || {};
        const items = matchedLot.items || [];
        const oosCount = items.filter((item) => !item.atp).length;
        const hasStock = items.some((item) => item.atp);
        return {
            id: lotId || '',
            numberOfSKU: items.length,
            currentPrice: !mapPrice ? parsePriceString(defaults) : '',
            listPrice: !mapPrice ? parsePriceString(original) || parsePriceString(defaults) : '',
            priceWithCoupon:
                !mapPrice && hasStock && isCouponAmount && fpac && fpac.length
                    ? decimalRounding(String(fpac[0].amount.min).replace('$', ''))
                    : '',
            pricingCadence: getPricingCadence(type, marketingLabel, promotions),
            skuPercentageOOS: items.length ? Math.round((oosCount / items.length) * 100) : 0,
        };
    });
};

/**
 * Reads productDetails & inventory from state and maps to CJA product schema.
 * Enriches with pricing, seller, and option selectors when available.
 */
const mapProductFromState = (state, sourceMiniPDP = false) => {
    const productDetails = sourceMiniPDP
        ? selectProductDetailsWrapper(sourceMiniPDP)(state) || {}
        : state.productDetails || {};
    const inventory = sourceMiniPDP
        ? selectInventoryWrapper(sourceMiniPDP)(state) || []
        : state.inventory || [];
    const hostname = (state.context || {}).hostname || 'www.jcpenney.com';
    const lots = productDetails.lots || [];
    const firstLot = lots[0] || {};
    const hasStock =
        inventory.length && inventory[0] && inventory[0].inventory
            ? inventory[0].inventory.some((item) => item.atp)
            : false;

    const baseProduct = {
        lotId: lots.map((lot) => lot.id),
        lotDetails: lots.map((lot) => ({
            id: lot.id || '',
            numberOfSKU: (lot.items || []).length,
        })),
        id: productDetails.id || '',
        name: (productDetails.name || '').toLowerCase(),
        link: productDetails.pdpURL ? `https://${hostname}${productDetails.pdpURL}` : '',
        inventoryStatus: hasStock ? 'in stock' : 'out of stock',
        storeAvailability: getStoreAvailability(firstLot.channelOffering),
        averageRating: productDetails.valuation?.rating ? `${productDetails.valuation.rating}` : '',
        review: productDetails.valuation?.reviews?.count
            ? `${productDetails.valuation.reviews.count}`
            : '',
        imgSource: (productDetails.images || [])[0]?.url || '',
        categoryId: productDetails.category?.id || '',
        departmentId: productDetails.category?.parent?.id || '',
        flags: {
            isCollection: !!(productDetails.collection && productDetails.collection.length),
            isCarePlan: !!firstLot.warranties,
            isBundle: productDetails.templateType === 'bundle',
        },
        type: productDetails.templateType || '',
        categoryName: productDetails.category?.name || '',
        brand: productDetails.brand?.name || '',
    };

    try {
        const pricing = productPricingSelector(sourceMiniPDP)(state) || {};
        const pricingForAllLots = productPricingSelectorForAllLot(sourceMiniPDP)(state) || [];
        const productOptions = initProductOptions(sourceMiniPDP)(state) || {};
        const marketPlaceInfo = selectMarketPlaceSellerInfo(sourceMiniPDP)(state) || {};
        const selectedColor = getSelectedColor(sourceMiniPDP)(state);
        const selectedSize = getSelectedSize(sourceMiniPDP)(state);

        const {
            defaults,
            original,
            type,
            surcharges,
            marketingLabel,
            mapPrice,
            promotions,
            fpac,
            isCouponAmount,
        } = pricing;
        const canShowPrice = !mapPrice;
        const sellingPrice = canShowPrice ? parsePriceString(defaults) : '';
        const listPrice = canShowPrice ? parsePriceString(original) || sellingPrice : '';
        const priceWithCoupon =
            canShowPrice && hasStock && isCouponAmount && fpac && fpac.length
                ? decimalRounding(String(fpac[0].amount.min).replace('$', ''))
                : '';

        const filteredSKU = (productOptions.filteredItems || [])[0] || {};
        const featureAttributes = (firstLot.productIcons || [])
            .flatMap((item) => (item.valueIcons || []).map((v) => v.value))
            .filter(Boolean);
        const hasEcoRebate = lots.some((lot) =>
            (lot.items || []).some((item) => item.rebatesAvailable)
        );
        const mpSellerName = marketPlaceInfo?.sellerName || productConstants.JCP_SELLER_NAME;

        return {
            ...baseProduct,
            lotDetails: buildLotDetailsFromPricing(pricingForAllLots, lots),
            listPrice,
            sellingPrice,
            surchargePrice: surcharges && surcharges.length ? surcharges : '0.00',
            pricingCadence: getPricingCadence(type, marketingLabel, promotions),
            priceWithCoupon,
            SKU: filteredSKU.id || '',
            size: selectedSize || '',
            color: selectedColor || '',
            marketingLabel: marketingLabel ? marketingLabel.toLowerCase() : '',
            itemType: (firstLot.itemType || '').toLowerCase(),
            vendorName: mpSellerName,
            badge: (productDetails.badges || [])[0] || '',
            feature: featureAttributes,
            sellerName: mpSellerName,
            sellerType: marketPlaceInfo?.isMarketPlaceProduct
                ? productConstants.MARKETPLACE_SELLER_TYPE
                : productConstants.JCP_SELLER_TYPE,
            flags: {
                ...baseProduct.flags,
                isGiftRegistry: !!(
                    productDetails.restrictions && productDetails.restrictions.canAddToRegistry
                ),
                isEcoRebate: hasEcoRebate,
                isCouponPricing: !!isCouponAmount,
                isUrgencyMessageShown: !!(
                    state.urgencyStatements &&
                    (state.urgencyStatements.viewMessage ||
                        state.urgencyStatements.addToCartMessage ||
                        state.urgencyStatements.soldMessage)
                ),
            },
            _matchedLotId: filteredSKU.id
                ? (
                      lots.find((lot) =>
                          (lot.items || []).some((item) => item.id === filteredSKU.id)
                      ) || {}
                  ).id || ''
                : '',
            _selectedQuantity: selectSelectedQuantityWrapper(sourceMiniPDP)(state),
        };
    } catch (e) {
        return baseProduct;
    }
};

// --- Payload Builders ---

const buildPageViewPayload = (state) => {
    const productDetails = state.productDetails || {};
    const breadcrumbs = productDetails?.breadCrumbInfo?.breadcrumbs || [];
    const shopTheLook = state.shopTheLook || {};

    return buildCJAPageViewPayload(
        {
            name: `d:pdp:${productDetails.name || ''}`.toLowerCase(),
            type: 'pdp',
            breadcrumbs: breadcrumbs
                .map((b) => b.breadCrumbLabel || '')
                .join('>')
                .toLowerCase(),
            siteSection: breadcrumbs[1]?.breadCrumbLabel || '',
            category: {
                primary: breadcrumbs[1]?.breadCrumbLabel || '',
                sub1: breadcrumbs[2]?.breadCrumbLabel || '',
                sub2: breadcrumbs[3]?.breadCrumbLabel || '',
            },
            flags: {
                isErrorPage: !!state.ErrorPage,
                isChatAvailable: true,
                isShopTheLookModule: !!(
                    shopTheLook.productPrices && shopTheLook.productPrices.length
                ),
            },
            tech: {
                releaseVersion: (state.context || {}).__RELEASEVERSION__ || '',
                version: `apollo 1 pdp - ${productDetails.templateType || 'standard'}`,
            },
        },
        {},
        {
            interactionName: 'product_view',
            products: buildProductData([mapProductFromState(state)]),
        }
    );
};

const buildAddToCartPayload = (state, payload, isUpdate) => {
    const sourceMiniPDP = payload.sourceMiniPDP || false;
    const mapped = mapProductFromState(state, sourceMiniPDP);
    const selectedQuantity = mapped._selectedQuantity;
    const selectedLot = filterToSelectedLot(mapped);

    return {
        event: 'add_to_cart',
        event_name: 'add_to_cart',
        cartAddType: isUpdate ? 'addtocart:update quantity' : 'addtocart',
        cartAddLocation: 'pdp',
        products: buildProductData([
            {
                ...mapped,
                ...selectedLot,
                inventoryStatus:
                    mapped.inventoryStatus === 'in stock' ? 'in_stock' : 'out_of_stock',
                quantity:
                    selectedQuantity && selectedQuantity !== 0 ? parseInt(selectedQuantity, 10) : 1,
                deliveryMethod: (payload.deliveryMethod || '').toLowerCase(),
                deliveryDays: payload.deliveryDays || '',
                deliveryDayType: payload.deliveryDayType || '',
                productClickPosition: payload.clickPosition || '',
                cartAddLocation: 'pdp',
                flags: {
                    ...mapped.flags,
                    isMaxQuantityReached: !!payload.maxQuantityMsgDisplay,
                },
            },
        ]),
    };
};

const getCurrentPageUrl = () =>
    typeof window !== 'undefined' && window.location ? window.location.pathname : '';

const buildProductOOSPayload = (state) =>
    buildCJAEvent({
        event: 'linkClick',
        event_name: 'linkClick',
        linkComponent: 'pdp',
        linkText: 'out of stock',
        linkType: 'other',
        linkUrl: getCurrentPageUrl(),
        linkModuleName: 'productOOS',
        linkRegion: 'product details',
        products: buildProductData([
            {
                ...mapProductFromState(state),
                inventoryStatus: 'out_of_stock',
            },
        ]),
    });

const buildErrorPayload = (errors) => ({
    event: 'error',
    event_name: 'error',
    error: Array.isArray(errors)
        ? errors.map((e) => ({
              type: 'pdp',
              id: e.errorID || '',
              message: e.errorDescription || e.productDescription || e.message || '',
          }))
        : [{ type: 'pdp', id: '', message: '' }],
});

const buildLinkClickPayload = (linkComponent, linkText, linkModuleName, extra) =>
    buildCJAEvent({
        event: 'linkClick',
        event_name: 'linkClick',
        linkComponent,
        linkText: linkText || '',
        linkType: 'other',
        linkUrl: getCurrentPageUrl(),
        linkModuleName: linkModuleName || '',
        ...extra,
    });

const buildSelectOptionsPDPPayload = (state, payload) => {
    const mapped = mapProductFromState(state);
    const selectedLot = filterToSelectedLot(mapped);

    return {
        event: 'selectoptionspdp',
        event_name: 'selectoptionspdp',
        interactionName: 'product_view',
        products: buildProductData([
            {
                ...mapped,
                ...selectedLot,
                lotId: selectedLot.lotId.length
                    ? selectedLot.lotId
                    : payload.productLotId || mapped.lotId,
                inventoryStatus:
                    mapped.inventoryStatus === 'in stock' ? 'in_stock' : 'out_of_stock',
            },
        ]),
    };
};

const buildSaveProductPayload = (actionPayload) => {
    const productInfo = (actionPayload?.product || [])[0]?.productInfo || {};
    return {
        event: 'save_product',
        event_name: 'save_product',
        products: buildProductData([
            {
                id: productInfo.productPPID || '',
                name: (productInfo.productName || '').toLowerCase(),
                imgSource: productInfo.productImageSrc || '',
                link: productInfo.productLink || '',
                sellerName: productInfo.mpSellername || '',
                sellerType: productInfo.productSellertype || '',
            },
        ]),
    };
};

// --- Middleware ---

let lastSelectOptionsSKU = null;

const CJAMiddleware = (store) => (next) => (action) => {
    if (typeof window === 'undefined') return next(action);
    if (!cjaAnalytics.isEnabled()) return next(action);

    const state = store.getState();
    let eventName = null;
    let eventData = null;

    switch (action.type) {
        case PDP_PAGE_LOAD_READY:
            eventName = 'pageView';
            eventData = buildPageViewPayload(state);
            break;

        // cartAdd → add_to_cart
        case PDP_CART_ADD_ANALYTICS:
        case BUNDLE_PDP_CART_ADD_ANALYTICS:
            eventName = 'add_to_cart';
            eventData = buildAddToCartPayload(state, action.payload || {}, false);
            break;

        // cartUpdate → add_to_cart (with cartAddType: 'addtocart:update quantity')
        case PDP_CART_UPDATE_ANALYTICS:
            eventName = 'add_to_cart';
            eventData = buildAddToCartPayload(state, action.payload || {}, true);
            break;

        // productOOS → linkClick
        case PDP_PRODUCT_OOS_ANALYTICS:
            eventName = 'linkClick';
            eventData = buildProductOOSPayload(state);
            break;

        // videoLoaded → linkClick
        case PDP_VIDEO_PLAYER_ANALYTICS:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'product media',
                action.payload?.videoName || action.payload?.videoId || '',
                'videoLoaded',
                { linkRegion: 'product media' }
            );
            break;

        case PDP_FORM_ERROR_ANALYTICS:
            eventName = 'error';
            eventData = buildErrorPayload(action.payload);
            break;

        // pdpToolClick → linkClick
        case PDP_TOOL_CLICK_ANALYTICS: {
            const toolName = action.payload || '';
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'product details',
                toolName,
                `pdpToolClick:${toolName}`,
                { linkRegion: 'product details' }
            );
            break;
        }

        // pdpKAClick → linkClick
        case analyticsActions.PDP_KA_CLICK_ANALYTICS: {
            const kaToolName = action.payload?.page?.pageInfo?.pdpToolName || '';
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'product details',
                kaToolName,
                `pdpKAClick:${kaToolName}`,
                { linkRegion: 'product details' }
            );
            break;
        }

        // sameDayPickup → linkClick
        case PDP_SAMEDAY_PICKUP_ANALYTICS:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'delivery options',
                'same day pickup',
                'sameDayPickup',
                { linkRegion: 'delivery options' }
            );
            break;

        // checkAvailability → linkClick
        case PDP_CHECK_AVAILABILITY_ANALYTICS:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'delivery options',
                'check availability',
                `checkAvailability:${(state.productDetails || {}).id || ''}`,
                { linkRegion: 'delivery options' }
            );
            break;

        // openModal → linkClick
        case PDP_OPEN_MODAL_ANALYTICS: {
            const modalName = action.payload || '';
            eventName = 'linkClick';
            eventData = buildLinkClickPayload('pdp', modalName, `openModal:${modalName}`, {
                linkRegion: 'pdp',
            });
            break;
        }

        // changeZipCode → linkClick
        case PDP_CHANGE_ZIPCODE_ANALYTICS:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'delivery options',
                'change zip code',
                'changeZipCode',
                { linkRegion: 'delivery options' }
            );
            break;

        // selectOptionsPDP → selectoptionspdp (all SKU dimensions selected, with products)
        // Deduplicate: PDP_ALL_SKU_SELECTED fires from both ProductOptions and DeliveryOption
        case analyticsActions.PDP_ALL_SKU_SELECTED: {
            const skuForSelect = action.payload?.productSKU || '';
            if (skuForSelect && skuForSelect === lastSelectOptionsSKU) break;
            lastSelectOptionsSKU = skuForSelect;
            eventName = 'selectoptionspdp';
            eventData = buildSelectOptionsPDPPayload(state, action.payload || {});
            break;
        }

        // carePlanView → linkClick (with selected lot product data)
        case PDP_CARE_PLAN_VIEW_ANALYTICS: {
            const viewMapped = mapProductFromState(state);
            eventName = 'linkClick';
            eventData = buildLinkClickPayload('care plan', 'care plan view', 'carePlanView', {
                linkRegion: 'care plan',
                products: buildProductData([
                    {
                        ...viewMapped,
                        ...filterToSelectedLot(viewMapped),
                    },
                ]),
            });
            break;
        }

        // carePlanAccept → linkClick (with selected lot product data)
        case PDP_CARE_PLAN_ACCEPT_ANALYTICS: {
            const acceptMapped = mapProductFromState(state);
            eventName = 'linkClick';
            eventData = buildLinkClickPayload('care plan', 'care plan accept', 'carePlanAccept', {
                linkRegion: 'care plan',
                products: buildProductData([
                    {
                        ...acceptMapped,
                        ...filterToSelectedLot(acceptMapped),
                    },
                ]),
            });
            break;
        }

        // storeSearch → linkClick
        case SEARCH_STORES_CLICK_EVENT: {
            const searchText = action.payload || '';
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'store locator',
                searchText,
                `storesearch:${searchText}`,
                { linkRegion: 'store locator' }
            );
            break;
        }

        case PDP_SHOP_THIS_LOOK_CLICK_EVENT:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'shop the look',
                action.payload?.linkText || action.payload?.moduleName || '',
                `shopTheLook:${action.payload?.shopTheLookID || ''}`,
                {
                    linkId: action.payload?.shopTheLookID || '',
                    linkRegion: 'pdp',
                    linkPosition: action.payload?.linkPosition ?? action.payload?.position ?? 0,
                }
            );
            break;

        case PDP_SHOP_THE_LOOK_MINI_PDP_CLICK_EVENT:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'shop the look',
                action.payload?.linkText || action.payload?.moduleName || '',
                `shopTheLookDetail:${action.payload?.shopTheLookID || ''}:${
                    action.payload?.shopTheLookPPID || ''
                }`,
                {
                    linkId: action.payload?.shopTheLookID || '',
                    linkRegion: 'mini pdp',
                    linkPosition: action.payload?.linkPosition ?? action.payload?.position ?? 0,
                }
            );
            break;

        case PDP_SHOP_THE_SET_CLICK_EVENT:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'shop the set',
                action.payload?.linkText || action.payload?.moduleName || '',
                `shopTheSet:${action.payload?.shopTheSetID || ''}:${
                    action.payload?.shopTheSetPPID || ''
                }`,
                {
                    linkId: action.payload?.shopTheSetID || '',
                    linkRegion: 'mini pdp',
                    linkPosition: action.payload?.linkPosition ?? action.payload?.position ?? 0,
                }
            );
            break;

        // vto → linkClick
        case PDP_ENABLE_VTO:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload('product media', 'vto', 'vto', {
                linkRegion: 'product media',
            });
            break;

        case PDP_PSC_AVAILABILITY:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'product details',
                action.payload?.linktext || '',
                action.payload?.name || '',
                { linkRegion: 'product details' }
            );
            break;

        // inventoryProductOOS → linkClick (with products)
        case PDP_PAGE_LOAD_PRODUCT_OOS:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload('pdp', 'out of stock', 'inventoryProductOOS', {
                linkRegion: 'product details',
                products: buildProductData([
                    {
                        ...mapProductFromState(state),
                        inventoryStatus: 'out_of_stock',
                    },
                ]),
            });
            break;

        // saveProduct → save_product
        case ADD_SITE_TO_LIST_EVENT:
            eventName = 'save_product';
            eventData = buildSaveProductPayload(action.payload);
            break;

        default:
            break;
    }

    if (eventName && eventData) {
        cjaAnalytics.track(eventName, eventData);
    }

    return next(action);
};

export default CJAMiddleware;
